/* eslint-disable react/prop-types */
import React, { useState, useCallback, memo } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { navItems, NavItem } from '../navConfig';
import { useNavigation } from '../hooks';
import { DarkModeToggle } from './DarkModeToggle';
import { SidebarAnimations } from './SidebarAnimations';
import mainLogo from '../../../../Assests/images/NavBarLogo.png';

interface TopSidebarProps {
  topbarHeight: number;
  expandedItems: Record<string, boolean>;
  setExpandedItems: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  isMobile: boolean;
  setSidebarOpen: (value: boolean) => void;
}

export const TopSidebar: React.FC<TopSidebarProps> = memo(({
  topbarHeight,
  expandedItems,
  setExpandedItems,
  isMobile,
  setSidebarOpen,
}) => {
  const { theme, darkMode, toggleDarkMode } = useTheme();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoveredChild, setHoveredChild] = useState<string | null>(null);

  const { toggleExpanded, closeExpanded, handleNavigate, isActive, isChildActive } = useNavigation({
    isMobile,
    setSidebarOpen,
    setExpandedItems,
  });

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, action: () => void) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        action();
      }
    },
    []
  );

  const handleNavItemClick = useCallback(
    (item: NavItem) => {
      if (item.children) {
        toggleExpanded(item.label);
      } else if (item.path) {
        handleNavigate(item.path);
      }
    },
    [toggleExpanded, handleNavigate]
  );

  const handleChildClick = useCallback(
    (childPath: string, parentLabel: string) => {
      handleNavigate(childPath);
      closeExpanded(parentLabel);
    },
    [handleNavigate, closeExpanded]
  );

  const getNavItemStyle = useCallback(
    (item: NavItem, isHovered: boolean): React.CSSProperties => {
      const active = isActive(item);
      return {
        padding: '8px 16px',
        borderRadius: '6px',
        color: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        background: active ? '#2563eb' : isHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
        fontSize: '14px',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        transition: 'all 0.2s ease',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        border: 'none',
      };
    },
    [isActive]
  );

  const getChildItemStyle = useCallback(
    (path: string, isHovered: boolean): React.CSSProperties => {
      const active = isChildActive(path);
      return {
        padding: '10px 16px',
        color: active ? '#fff' : darkMode ? '#94a3b8' : 'rgba(255,255,255,0.85)',
        background: active ? 'rgba(37,99,235,0.5)' : isHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
        cursor: 'pointer',
        fontSize: '13px',
        transition: 'all 0.2s ease',
        transform: isHovered ? 'translateX(4px)' : 'translateX(0)',
        border: 'none',
        width: '100%',
        textAlign: 'left',
      };
    },
    [isChildActive, darkMode]
  );

  return (
    <header
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        right: 0,
        width: '100vw',
        height: `${topbarHeight}px`,
        background: theme.sidebar,
        display: 'flex',
        alignItems: 'center',
        zIndex: 1000,
        borderBottom: `1px solid ${theme.border}`,
        boxSizing: 'border-box',
        animation: 'sidebarSlideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
      role="navigation"
      aria-label="Top navigation"
    >
      {/* Logo */}
      <div style={{
        padding: '0 20px',
        borderRight: `1px solid ${theme.border}`,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        animation: 'sidebarFadeIn 0.5s ease',
      }}>
        <img src={mainLogo} alt="Logo" style={{ height: '36px', objectFit: 'contain' }} />
      </div>

      {/* Nav */}
      <nav
        style={{ display: 'flex', alignItems: 'center', padding: '0 16px', gap: '4px' }}
        aria-label="Main navigation"
      >
        {navItems.map((item, index) => (
          <div
            key={item.label}
            style={{
              position: 'relative',
              animation: `sidebarSlideInTop 0.4s ease ${index * 0.08}s backwards`,
            }}
          >
            <button
              onClick={() => handleNavItemClick(item)}
              onKeyDown={(e) => handleKeyDown(e, () => handleNavItemClick(item))}
              onMouseEnter={() => setHoveredItem(item.label)}
              onMouseLeave={() => setHoveredItem(null)}
              aria-expanded={item.children ? expandedItems[item.label] : undefined}
              aria-haspopup={item.children ? 'menu' : undefined}
              style={getNavItemStyle(item, hoveredItem === item.label)}
            >
              {item.label}
              {item.children && (
                <span
                  style={{
                    fontSize: '10px',
                    transition: 'transform 0.3s ease',
                    transform: expandedItems[item.label] ? 'rotate(180deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                  }}
                  aria-hidden="true"
                >
                  ▼
                </span>
              )}
            </button>
            {item.children && expandedItems[item.label] && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  background: theme.sidebar,
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  minWidth: '150px',
                  marginTop: '4px',
                  zIndex: 1001,
                  animation: 'sidebarDropdownSlide 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transformOrigin: 'top',
                }}
                role="menu"
                aria-label={`${item.label} submenu`}
              >
                {item.children.map((child, childIndex) => (
                  <button
                    key={child.path}
                    onClick={() => handleChildClick(child.path, item.label)}
                    onKeyDown={(e) => handleKeyDown(e, () => handleChildClick(child.path, item.label))}
                    onMouseEnter={() => setHoveredChild(child.path)}
                    onMouseLeave={() => setHoveredChild(null)}
                    role="menuitem"
                    style={{
                      ...getChildItemStyle(child.path, hoveredChild === child.path),
                      animation: `sidebarFadeInScale 0.2s ease ${childIndex * 0.05}s backwards`,
                    }}
                  >
                    {child.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Dark Mode */}
      <div style={{
        marginLeft: 'auto',
        marginRight: '140px',
        padding: '0 20px',
        borderLeft: `1px solid ${theme.border}`,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        animation: 'sidebarFadeIn 0.5s ease 0.3s backwards',
      }}>
        <DarkModeToggle darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      </div>

      <SidebarAnimations />
    </header>
  );
});

TopSidebar.displayName = 'TopSidebar';
